import {
  forwardRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
} from "react";
import { cn } from "../lib/utils";

export const CELL_STATES = [
  "welded",
  "tacked",
  "working",
  "hidden",
  "unlit",
  "stale",
  "error",
] as const;

export type CellState = (typeof CELL_STATES)[number];

export interface CellProps extends HTMLAttributes<HTMLSpanElement> {
  state?: CellState;
  hue?: string;
  cursor?: boolean;
  label?: string;
}

function cellStyle(hue: string | undefined, style: CSSProperties | undefined): CSSProperties {
  return hue === undefined
    ? { ...style }
    : { "--sqk-cell-color": hue, ...style } as CSSProperties;
}

export const Cell = forwardRef<HTMLSpanElement, CellProps>(({
  state = "welded",
  hue,
  cursor = false,
  label,
  className,
  style,
  ...props
}, ref) => (
  <span
    aria-label={label ?? state}
    className={cn("sqk-cell", className)}
    data-cursor={cursor ? "true" : undefined}
    data-state={state}
    ref={ref}
    role={props.role ?? "img"}
    style={cellStyle(hue, style)}
    {...props}
  />
));
Cell.displayName = "Cell";

export const CellButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  state?: CellState;
  hue?: string;
  cursor?: boolean;
}>(({
  state = "welded",
  hue,
  cursor = false,
  className,
  style,
  type,
  ...props
}, ref) => (
  <button
    className={cn("sqk-cell sqk-cell--interactive", className)}
    data-cursor={cursor ? "true" : undefined}
    data-state={state}
    ref={ref}
    style={cellStyle(hue, style)}
    type={type ?? "button"}
    {...props}
  />
));
CellButton.displayName = "CellButton";

export function CellGrid({
  columns,
  className,
  style,
  ...props
}: HTMLAttributes<HTMLDivElement> & { columns?: number }) {
  const gridStyle = columns === undefined
    ? style
    : { "--sqk-cell-columns": columns, ...style } as CSSProperties;
  return <div className={cn("sqk-cell-grid", className)} style={gridStyle} {...props} />;
}
